// In class — the student's live screen.
//
// A pure function of what the server says is happening, in the order a class
// actually runs: waiting → pulse question(s) → end-of-class quiz → reflection
// → done. Students never navigate during class; the screen changes when the
// professor's Run Class actions move things forward. Pulse options are
// shuffled per student (presentation only; the server grades by key), so
// "pick number 2" means nothing to the person next to them.
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import type { ComponentChildren } from "preact";
import { context } from "../../state/session";
import { t, lang, apiErrorText } from "../../i18n";
import { ApiError } from "../../api/client";
import type { ClassGrade } from "../../api/types";
import { formatGrade } from "../../features/classRecord/format";
import { currentPulse, answerPulse, shuffleOptions, type StudentPulseView } from "../../api/pulse";
import { QuizPlayer } from "../../features/quiz/Player";
import { RankBanner } from "../../features/quiz/RankBanner";
import { Reflection } from "../../features/reflection/Reflection";
import {
  clearJoinedClassSession,
  joinedClassSessionId
} from "../../api/session";
import {
  fallbackLiveSessionId,
  selectLiveSessionId
} from "../../features/join/sessionState";

const POLL_MS = 3000;

// Defined at module scope, not inside Live(), so its identity is stable across
// the 3s poll re-renders. A component defined inline inside another component
// gets a fresh function reference every render — Preact then treats each
// render as a different component type and unmounts/remounts everything
// inside it, which silently reset the quiz player before it ever finished
// loading (a real bug caught while testing this).
function LiveShell({ error, children }: { error: string | null; children: ComponentChildren }) {
  return (
    <div class="stack">
      <div class="row" style="justify-content: space-between;">
        <div>
          <p class="eyebrow">{t("live.eyebrow")}</p>
          <h1>{t("live.title")}</h1>
        </div>
        <a class="btn quiet" href="/">{t("live.backToToday")}</a>
      </div>
      {error ? <p class="error-text" role="alert">{error}</p> : null}
      {children}
    </div>
  );
}

export function Live() {
  const ctx = context.value;
  const studentSessions = ctx?.student_sessions ?? [];
  const [sessionId, setSessionId] = useState<string | null>(() =>
    selectLiveSessionId(studentSessions, joinedClassSessionId())
  );

  const [view, setView] = useState<StudentPulseView | null>(null);
  const [busy, setBusy] = useState(false);
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const [reflectionDone, setReflectionDone] = useState(false);
  // The grade the server posted as the reflection landed. Held here rather than
  // re-fetched: the student has just finished, and sending them to My Grades to
  // find out how they did is the round trip this whole change removes.
  const [classGrade, setClassGrade] = useState<ClassGrade | null>(null);
  // Until the first poll answers, the screen is loading — showing "waiting for
  // the professor" before we have ever asked the server misreads a slow network
  // as an idle classroom.
  const [firstPollDone, setFirstPollDone] = useState(false);
  const shownAt = useRef<number>(Date.now());
  const poll = useRef<number | undefined>(undefined);
  const clock = useRef<number | undefined>(undefined);

  async function refresh() {
    if (!sessionId) return;
    try {
      const next = await currentPulse(sessionId);
      setView((prev) => {
        if (next.round && next.round.round_id !== prev?.round?.round_id) {
          shownAt.current = Date.now(); // start the latency clock for this question
          setPendingKey(null);
        }
        return next;
      });
    } catch (e) {
      // A mid-attempt quiz never loses its screen to a poll error. On
      // 2026-08-20 three students were thrown out of the end-of-class quiz by
      // exactly this: their one-hour token lapsed at quiz time, the poll got
      // an auth error, and the branch below read it as "not in this class" —
      // join cleared, view blanked, player unmounted, work gone. While the
      // player is live, every poll failure is treated as a blip: keep the last
      // view, keep the join, let the next poll (with a refreshed token) recover.
      if (quizHold.current) return;
      const joinedSessionId = joinedClassSessionId();
      if (
        joinedSessionId === sessionId &&
        e instanceof ApiError &&
        [400, 403, 404].includes(e.status)
      ) {
        clearJoinedClassSession();
        setView(null);
        setError(null);
        setSessionId(fallbackLiveSessionId(studentSessions, sessionId));
        return;
      }
      // Keep the last known state on a network blip rather than blanking the screen.
      if (!view) setError(apiErrorText(e, "live.loadFailed"));
    } finally {
      setFirstPollDone(true);
    }
  }

  useEffect(() => {
    if (!sessionId) {
      setSessionId(selectLiveSessionId(studentSessions, joinedClassSessionId()));
    }
  }, [sessionId, studentSessions]);

  useEffect(() => {
    void refresh();
    poll.current = setInterval(refresh, POLL_MS) as unknown as number;
    clock.current = setInterval(() => setNow(Date.now()), 1000) as unknown as number;
    return () => {
      clearInterval(poll.current);
      clearInterval(clock.current);
    };
  }, [sessionId]);

  const round = view?.round ?? null;
  const mine = view?.my_answer ?? null;

  // Who owns the screen at the moment the quiz closes.
  //
  // Rendering the player on `state === "live"` alone made the class poll able to
  // unmount it mid-submit: the poll is the very thing that flips the state, and
  // it races the player's own 1-second auto-submit clock. Whenever the poll won,
  // the student's whole attempt vanished — no error, no rank, no quiz mark.
  //
  // So the player, not the poll, decides when it is done. Live remembers which
  // instance it has shown the player for and which one the player has reported
  // finishing, and keeps it on screen in between whatever the state says.
  const quizInstanceId = view?.quiz.instance_id ?? null;
  const quizState = view?.quiz.state ?? null;
  const [quizStartedFor, setQuizStartedFor] = useState<string | null>(null);
  const [quizFinishedFor, setQuizFinishedFor] = useState<string | null>(null);

  // The gates and the pulse question outrank the quiz, so "started" is only
  // recorded when the quiz branch is actually the one being reached — otherwise
  // a pulse question pushed during a quiz would mark a player that never mounted.
  const quizBranchReachable =
    !!view && view.checked_in && view.session_state !== "paused" && !round;

  useEffect(() => {
    if (quizBranchReachable && quizInstanceId && quizState === "live") {
      setQuizStartedFor(quizInstanceId);
    }
  }, [quizBranchReachable, quizInstanceId, quizState]);

  // Unfinished means: this screen mounted a player for an instance and the
  // player has not reported done. The view being momentarily null (a poll
  // error) or naming the SAME instance both keep it unfinished; only a
  // different instance — a genuinely new quiz — releases the hold.
  const quizUnfinished =
    quizStartedFor !== null &&
    quizFinishedFor !== quizStartedFor &&
    (quizInstanceId === null || quizInstanceId === quizStartedFor);

  // The poll's catch runs in a closure from the render that armed the
  // interval, so it cannot read fresh state — this ref is how it learns a
  // quiz is mid-attempt RIGHT NOW and must not be disturbed.
  const quizHold = useRef(false);
  quizHold.current = quizUnfinished;
  const profileId = ctx?.profile?.id ?? "anon";
  const useSpanish = lang.value === "es";

  const displayOptions = useMemo(
    () => (round ? shuffleOptions(round.options, `${round.round_id}:${profileId}`) : []),
    [round?.round_id, profileId]
  );

  const remaining = round ? Math.max(0, Math.round((new Date(round.ends_at).getTime() - now) / 1000)) : 0;

  async function submit(optionKey: string) {
    if (!round) return;
    setBusy(true);
    setPendingKey(optionKey); // paint the choice immediately; the server confirms behind it
    setError(null);
    try {
      await answerPulse({
        round_id: round.round_id,
        option_key: optionKey,
        latency_ms: Date.now() - shownAt.current
      });
      await refresh();
    } catch (e) {
      setPendingKey(null); // the tap did not land — put the choices back
      setError(apiErrorText(e, "live.answerFailed"));
    } finally {
      setBusy(false);
    }
  }

  if (!sessionId) {
    return (
      <div class="stack">
        <div class="empty-state card">
          <h3>{t("live.noClass")}</h3>
          <p>{t("live.noClassBody")}</p>
          <a class="btn" href="/">{t("live.backToToday")}</a>
        </div>
      </div>
    );
  }

  // A player that has started outranks EVERYTHING below — the check-in gate,
  // the pause screen, even a pulse question pushed mid-quiz. Unmounting a
  // mid-attempt player throws away the student's clock and screen for reasons
  // that are never worth it: the server enforces the real rules (check-in,
  // windows, grading), and the player reports itself done. Before this hold,
  // any flicker in the poll's answer yanked the quiz off a student's phone.
  // First mount still goes through the gated branch further down — this arm
  // only ever KEEPS a player that legitimately started.
  const heldQuizId =
    quizUnfinished && quizStartedFor
      ? quizStartedFor
      : null;
  if (heldQuizId) {
    return (
      <LiveShell error={null}>
        <div class="card">
          <QuizPlayer
            activityInstanceId={heldQuizId}
            quizClosed={quizInstanceId === heldQuizId && quizState === "closed"}
            onFinished={() => setQuizFinishedFor(heldQuizId)}
            myRace={view?.quiz.my_race ?? null}
            round={view?.quiz.round ?? null}
          />
        </div>
      </LiveShell>
    );
  }

  // The gate, and it sits ahead of every branch below on purpose: the pulse
  // question, the quiz, and the reflection all hang off this screen, so gating
  // any one of them individually would leave the other two open.
  //
  // `checked_in` comes from the server, not from localStorage — a student on a
  // borrowed laptop or in private browsing has no stored join but does have an
  // attendance row, and only the server knows it. The matching refusals live in
  // course-pulse, course-activity-attempt and course-exit-ticket, so this is the
  // courteous message rather than the enforcement.
  if (view && !view.checked_in) {
    return (
      <LiveShell error={error}>
        <div class="empty-state card">
          <h3>{t("live.scanToJoin")}</h3>
          <p>{t("live.scanToJoinBody")}</p>
          <a class="btn" href="/">{t("live.backToToday")}</a>
        </div>
      </LiveShell>
    );
  }

  // Paused, not over. Ranked above every activity branch because while a class
  // is paused none of them can move: the server refuses a new question, and the
  // quiz and reflection belong to a class that has not finished. The poll keeps
  // running underneath, so the moment the professor resumes, the next question
  // lands here with nothing for the student to tap.
  if (view?.session_state === "paused") {
    return (
      <LiveShell error={error}>
        <div class="empty-state card">
          <h3>{t("live.pausedTitle")}</h3>
          <p>{t("live.pausedBody")}</p>
        </div>
      </LiveShell>
    );
  }

  // 1. A pulse question is on screen — highest priority, matches class rhythm.
  if (round) {
    return (
      <LiveShell error={error}>
        <div class="card">
          <div class="row" style="justify-content: space-between;">
            <p class="eyebrow">{t("live.answer")}</p>
            {round.state === "open" ? (
              <span class={`pill ${remaining > 5 ? "live" : "warn"}`}>
                {remaining > 0
                  ? mine
                    ? t("live.revealIn", { seconds: remaining })
                    : t("run.timeLeft", { seconds: remaining })
                  : t("live.timeUp")}
              </span>
            ) : null}
          </div>

          <h2 style="font-size: 1.35rem;">
            {(useSpanish && round.text_es) || round.text}
          </h2>

          {round.state === "revealed" ? (
            <div class="stack">
              {mine ? (
                <p class={mine.is_correct ? "pulse-verdict good" : "pulse-verdict bad"}>
                  {mine.is_correct ? t("live.youWereRight") : t("live.youWereWrong")}
                  {typeof mine.points_awarded === "number"
                    ? ` · ${t("live.pointsEarned", { points: mine.points_awarded })}`
                    : ""}
                </p>
              ) : null}
              <p class="hint">{t("live.correctWas")}</p>
              {round.options
                .filter((option) => option.key === round.correct_key)
                .map((option) => (
                  <div key={option.key} class="pulse-choice correct">
                    {(useSpanish && option.text_es) || option.text}
                  </div>
                ))}
            </div>
          ) : mine ? (
            <div class="stack">
              <p class="pulse-verdict">{t("live.recorded")}</p>
              <p class="hint">{t("live.recordedBody")}</p>
            </div>
          ) : (
            <div class="stack">
              {displayOptions.map((option) => (
                <button
                  key={option.key}
                  class={`pulse-choice tappable${pendingKey === option.key ? " selected" : ""}`}
                  type="button"
                  disabled={busy || remaining <= 0}
                  onClick={() => submit(option.key)}
                >
                  {(useSpanish && option.text_es) || option.text}
                </button>
              ))}
              {busy ? <p class="hint">{t("live.sending")}</p> : null}
              {remaining <= 0 ? <p class="hint">{t("live.timeUp")}</p> : null}
            </div>
          )}
        </div>
      </LiveShell>
    );
  }

  // 2. The end-of-class quiz is live — take it. This is the FIRST mount, and
  //    it is properly gated (checked in, not paused, no pulse question). From
  //    the moment the player mounts, the hold above the gates owns the screen:
  //    it keeps the player through the close, through poll errors, and through
  //    anything else, until the player reports it has sent what it had. The
  //    `live` test also keeps the score on screen for a student who submitted
  //    early, before the quiz closed at all.
  if (quizInstanceId && quizState === "live") {
    return (
      <LiveShell error={error}>
        <div class="card">
          <QuizPlayer
            activityInstanceId={quizInstanceId}
            quizClosed={false}
            onFinished={() => setQuizFinishedFor(quizInstanceId)}
            myRace={view?.quiz.my_race ?? null}
            round={view?.quiz.round ?? null}
          />
        </div>
      </LiveShell>
    );
  }

  // 3. The quiz has closed and the reflection isn't in yet — write it. Reached
  //    once the player is finished, and immediately for a student who joined
  //    after the quiz closed and never started it — they must land on the exit
  //    ticket, not on a dead screen.
  if (view?.quiz.instance_id && view.quiz.state === "closed" && view.reflection && !view.reflection.submitted && !reflectionDone) {
    return (
      <LiveShell error={error}>
        <div class="card">
          {view.quiz.my_rank ? <RankBanner rank={view.quiz.my_rank} /> : null}
          <Reflection
            classSessionId={sessionId}
            minWords={view.reflection.min_words}
            maxWords={view.reflection.max_words}
            onSubmitted={(grade) => {
              setClassGrade(grade);
              setReflectionDone(true);
            }}
          />
        </div>
      </LiveShell>
    );
  }

  // 4. Reflection submitted, or nothing scheduled yet — done for this class.
  //
  // The grade is on this screen because this is where the student is standing
  // when it becomes real. It used to be posted by hand from the class record,
  // so "you're done, go and look at My Grades" sent a room full of students to
  // an empty screen.
  if (reflectionDone || view?.reflection?.submitted) {
    return (
      <LiveShell error={error}>
        <div class="empty-state card">
          {view?.quiz?.my_rank ? <RankBanner rank={view.quiz.my_rank} /> : null}
          <h3>{t("live.doneTitle")}</h3>
          {classGrade && classGrade.grade !== null ? (
            <>
              <p class="live-grade-value" data-testid="live-class-grade">
                {formatGrade(classGrade.grade)}
              </p>
              <p class="live-grade-label">{t("live.gradeLabel")}</p>
              <p class="hint">
                {t("live.gradeBreakdown", {
                  pulseCorrect: classGrade.pulse_correct,
                  pulseTotal: classGrade.pulse_total,
                  quizCorrect: classGrade.quiz_correct,
                  quizTotal: classGrade.quiz_total
                })}
              </p>
            </>
          ) : (
            <p>{t("live.doneBody")}</p>
          )}
          <a class="btn" href="/grades">{t("live.viewGrades")}</a>
        </div>
      </LiveShell>
    );
  }

  return (
    <LiveShell error={error}>
      <div class="empty-state card">
        {firstPollDone || !sessionId ? (
          <>
            <h3>{t("live.waitingTitle")}</h3>
            <p>{t("live.waitingBody")}</p>
          </>
        ) : (
          <p class="hint">{t("app.loading")}</p>
        )}
      </div>
    </LiveShell>
  );
}
